import { useEffect, useState } from 'react';
import { useStore } from '@/store';
import TimelineView from '@/components/TimelineView';
import OverlapFinder from '@/components/OverlapFinder';
import QuickAddForm from '@/components/QuickAddForm';
import { useActivityHeartbeat, useActivityStatus } from '@/hooks/useActivityHeartbeat';
import { formatTime } from '@/lib/timezone';
import { usePageTracking } from '@/hooks/useAnalytics';

export default function Dashboard() {
  const { me, people, settings } = useStore();
  const [isWallpaperMode, setIsWallpaperMode] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Track page view
  usePageTracking('dashboard');
  
  // Send heartbeat when logged in (for live activity status)
  useActivityHeartbeat();
  
  // Fetch activity status for shared teammates
  useActivityStatus();
  
  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Detect fullscreen for wallpaper mode
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsWallpaperMode(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div className="space-y-4">
      {/* Clock Display - always visible */}
      {!isWallpaperMode && (
        <div className="text-center py-4">
          <div className="text-5xl md:text-6xl font-bold font-mono tracking-tight text-primary mb-1">
            {formatTime(currentTime, me.timezone, { use24Hour: settings.use24Hour, showSeconds: false })}
          </div>
          <div className="text-base md:text-lg text-text-light-muted dark:text-text-muted">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <div className="text-sm text-text-light-dim dark:text-text-dim mt-0.5">
            {me.timezone.replace('_', ' ')}
          </div>
        </div>
      )}
      
      {/* Timeline - primary focus, shown immediately */}
      <TimelineView />
      
      {/* Compact Input Section - minimal, non-intrusive */}
      {!isWallpaperMode && (
        <div className="space-y-3">
          {/* Quick Add Form */}
          <QuickAddForm />
        </div>
      )}
      
      {/* Overlap Finder - hide in wallpaper mode, show only if people exist */}
      {!isWallpaperMode && people.length > 0 && <OverlapFinder />}
      
      {/* Relaxation Video Section - hide in wallpaper mode */}
      {!isWallpaperMode && (
        <div className="mt-8 pt-6 border-t border-gray-100 dark:border-white/5">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/logo.svg" alt="RemoteTime" className="w-10 h-10" />
              <h3 className="text-xl font-semibold text-text-light dark:text-text">
                Take a Moment to Relax
              </h3>
            </div>
            <p className="text-sm text-text-light-muted dark:text-text-muted">
              Remote work can be intense. Enjoy this curated collection of calming videos to recharge.
            </p>
            <div className="rounded-lg overflow-hidden shadow-lg bg-bg-light-secondary dark:bg-bg-secondary">
              <div className="relative pb-[56.25%]">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/wO87dl2DKAU?controls=1&modestbranding=1&rel=0"
                  title="Relaxation Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
